import { useEffect, useState, useCallback, useRef } from "react";
import { Trophy, Clock, Radio } from "lucide-react";
import { FEATURES } from "@/lib/features";

const API = process.env.REACT_APP_BACKEND_URL;
const ACTIVE_POLL_MS = 5000;
const LEADERBOARD_POLL_MS = 5000;

export function useActiveTournament(user, gameStateRef) {
  const [active, setActive] = useState(null);
  const [pending, setPending] = useState(null); // joined but not started yet
  const [liveBoard, setLiveBoard] = useState([]);
  const submittedRef = useRef(new Set()); // guard: avoid re-submitting same score

  const load = useCallback(async () => {
    if (!user || !FEATURES.rooms) { setActive(null); setPending(null); return; }
    try {
      const r = await fetch(`${API}/api/rooms/me/active`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        const all = d.active || [];
        // Prefer currently-active (not upcoming) tournament
        const running = all.find(t => t.starts_in_seconds <= 0 && t.ends_in_seconds > 0);
        const waiting = all.find(t => t.starts_in_seconds > 0);
        setActive(running || null);
        setPending(running ? null : (waiting || null));
      }
    } catch (err) { console.warn("[tournament] active load failed:", err); }
  }, [user]);

  useEffect(() => { load(); const t = setInterval(load, ACTIVE_POLL_MS); return () => clearInterval(t); }, [load]);

  // Live leaderboard polling — only while active tournament exists
  useEffect(() => {
    if (!active) { setLiveBoard([]); return; }
    let alive = true;
    const fetchBoard = async () => {
      try {
        const r = await fetch(`${API}/api/rooms/${active.room_id}/leaderboard`);
        if (r.ok && alive) setLiveBoard((await r.json()).leaderboard || []);
      } catch { /* ignore */ }
    };
    fetchBoard();
    const t = setInterval(fetchBoard, LEADERBOARD_POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [active]);

  const submitScore = useCallback(async ({ score, level, durationSeconds }) => {
    if (!active) return;
    const key = `${active.room_id}:${score}:${level}`;
    if (submittedRef.current.has(key)) return;
    submittedRef.current.add(key);
    try {
      await fetch(`${API}/api/rooms/${active.room_id}/score`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ score, level, duration_seconds: durationSeconds, session_token: active.session_token }),
      });
      // Refresh rank
      await load();
    } catch (err) { console.warn("[tournament] score submit failed:", err); }
  }, [active, load]);

  return { active, pending, liveBoard, submitScore, refresh: load };
}

export function TournamentHUD({ active }) {
  if (!active) return null;
  const min = Math.floor(active.ends_in_seconds / 60);
  const sec = active.ends_in_seconds % 60;
  return (
    <div className="inline-flex items-center gap-3 px-3 py-2 rounded-sm border border-amber-300/60 bg-black/70 backdrop-blur shadow-lg shadow-amber-300/20 font-mono text-[10px] tracking-widest" data-testid="tournament-hud">
      <Radio className="h-3.5 w-3.5 text-amber-200 animate-pulse" />
      <span className="text-amber-200 font-black">{active.label}</span>
      <span className="text-white/60">|</span>
      <span className="inline-flex items-center gap-1 text-amber-200">
        <Clock className="h-3 w-3" /> {min}:{sec.toString().padStart(2, "0")}
      </span>
      <span className="text-white/60">|</span>
      <span className="inline-flex items-center gap-1 text-amber-200">
        <Trophy className="h-3 w-3" /> RANK {active.rank || "-"}º/{active.player_count}
      </span>
    </div>
  );
}

export function TournamentPendingHUD({ pending }) {
  if (!pending) return null;
  const s = Math.max(0, pending.starts_in_seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const label = h > 0 ? `${h}h ${m}min` : `${m}:${sec.toString().padStart(2, "0")}`;
  return (
    <div className="inline-flex items-center gap-3 px-3 py-2 rounded-sm border border-orange-400/60 bg-black/70 backdrop-blur shadow-lg shadow-orange-400/20 font-mono text-[10px] tracking-widest" data-testid="tournament-pending-hud">
      <Clock className="h-3.5 w-3.5 text-orange-300 animate-pulse" />
      <span className="text-orange-300 font-black">{pending.label} · AGUARDANDO</span>
      <span className="text-white/60">|</span>
      <span className="text-orange-200">COMEÇA EM {label}</span>
      <span className="text-white/60">|</span>
      <span className="text-white/60">SCORE NÃO CONTA</span>
    </div>
  );
}

/** Compact side panel showing live top-5 during a tournament run. */
export function TournamentLiveBoard({ liveBoard, currentUserId }) {
  if (!liveBoard || liveBoard.length === 0) return null;
  const top = liveBoard.slice(0, 5);
  return (
    <div className="rounded-sm border border-amber-300/40 bg-black/70 backdrop-blur p-2 font-mono text-[10px] tracking-widest w-44" data-testid="tournament-live-board">
      <div className="flex items-center gap-1 text-amber-200 mb-1">
        <Trophy className="h-3 w-3" /> RANKING AO VIVO
      </div>
      <table className="w-full">
        <tbody>
          {top.map((p, i) => {
            const isMe = currentUserId && p.user_id === currentUserId;
            return (
              <tr key={p.user_id} className={isMe ? "text-primary" : "text-white/80"}>
                <td className="text-secondary pr-1">{i + 1}º</td>
                <td className="truncate max-w-[80px]">{p.display_name}</td>
                <td className="text-right text-amber-200">{(p.best_score || 0).toLocaleString()}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
